/** Inputs for mapping a pointer position inside the day-column background
 *  layer to a 15-minute-snapped session start, clamped inside the working
 *  window. Every value is derived from the background layer's own
 *  bounds/total minutes — never a library-internal element. */
export interface CursorToStartParams {
  /** Pointer y, relative to the background layer's own top edge. */
  y: number;
  /** The background layer's own rendered height, in px. */
  columnHeight: number;
  /** Minutes spanned by the working window (the layer's `totalMinutes`). */
  totalMinutes: number;
  /** The active session's duration, in minutes. */
  durationMinutes: number;
  /** Instant of the working window's start (top of the column). */
  windowStart: Date;
  /** No preview when there's nothing left to place. */
  hasCandidates: boolean;
}

const GRID_MINUTES = 15;

/** Snapped start with the session centered under the cursor, or `null` when there's nothing to
 *  preview (out of bounds, or no candidates left to place). */
export function cursorToStart(params: CursorToStartParams): Date | null {
  const { y, columnHeight, totalMinutes, durationMinutes, windowStart, hasCandidates } = params;
  if (!hasCandidates) return null;
  if (y < 0 || y > columnHeight) return null;

  const rawMinutes = (y / columnHeight) * totalMinutes - durationMinutes / 2;
  const snapped = Math.round(rawMinutes / GRID_MINUTES) * GRID_MINUTES;
  // Keep the whole session inside the window.
  const clamped = Math.max(0, Math.min(snapped, Math.max(0, totalMinutes - durationMinutes)));
  return new Date(windowStart.getTime() + clamped * 60_000);
}
